import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";
import {
    getBilledAmount,
    getGrossBilledAmount,
    getVatAmount,
    hasVatIncluded,
} from "../shared/vatDisplay";
import {
    getBuyingRate,
    getCost,
    getCostCurrency,
    getCurrency,
    getOperationalCost,
    getRevenue,
} from "../shared/jobFinancials";
import { bankRowsForInvoice, isForeignCountry, resolveCustomerCountry } from "../shared/bankAccounts";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    Add,
    ArrowDownward,
    ArrowUpward,
    BusinessCenter,
    Download,
    DirectionsBoat,
    Edit,
    Flight,
    FilterAlt,
    Inventory2,
    LocalShipping,
    Paid,
    PictureAsPdf,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    TrendingDown,
    TrendingUp,
    Visibility,
    WarningAmber,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatPercent } from "../utils/formatter";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CheckCircle } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const RISK_OPTIONS = [
    {
        value: "normal",
        label: "Normal",
        color: "#027A48",
        background: "#ECFDF3",
        border: "#ABEFC6",
    },
    {
        value: "overdue",
        label: "Overdue",
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    {
        value: "doubtful",
        label: "Doubtful",
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    {
        value: "bad_debt",
        label: "Bad Debt",
        color: "#B42318",
        background: "#FEF3F2",
        border: "#FECDCA",
    },
    {
        value: "written_off",
        label: "Written Off",
        color: "#344054",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
];

const normalizeRiskStatus = (value) => {
    const normalized = String(value || "normal")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    const aliases = {
        watch: "overdue",
        high: "doubtful",
        high_risk: "doubtful",
        highrisk: "doubtful",
        legal: "bad_debt",
        write_off: "written_off",
        writeoff: "written_off",
    };

    const resolvedStatus = aliases[normalized] || normalized;

    return RISK_OPTIONS.some((option) => option.value === resolvedStatus)
        ? resolvedStatus
        : "normal";
};

const getCollectionRisk = (job) => {
    const risk =
        job?.collectionRisk ||
        job?.collection_risk ||
        job?.invoice?.collectionRisk ||
        job?.invoice?.collection_risk ||
        {};
    const status = normalizeRiskStatus(
        risk?.status ||
        job?.riskStatus ||
        job?.risk_status ||
        job?.invoice?.riskStatus ||
        job?.invoice?.risk_status,
    );

    return {
        ...risk,
        status,
        reason: risk?.reason || "",
        notes: risk?.notes || "",
        provisionAmount: toNumber(risk?.provisionAmount ?? risk?.provision_amount),
    };
};

const getRiskOption = (job) => {
    const status = getCollectionRisk(job).status;
    return (
        RISK_OPTIONS.find((option) => option.value === status) || RISK_OPTIONS[0]
    );
};

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

// Financial payloads frequently include empty numeric fields as 0. For an
// invoice total, a placeholder 0 must not hide the next real selling value.
const firstPositiveNumber = (...values) =>
    values.map(toNumber).find((value) => value > 0) || 0;

const getPaidAmount = (job) =>
    Number(job?.paidAmount || job?.paid_amount || job?.collectedAmount || 0);

const getEntityId = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }
    return String(value).trim();
};

const getCustomerInvoices = (job) => {
    const invoices =
        job?.customerInvoices || job?.customer_invoices || job?.invoices || [];

    return Array.isArray(invoices) ? invoices : [];
};

const getPrimaryInvoice = (job) => {
    const invoices = getCustomerInvoices(job);

    return (
        invoices.find(
            (invoice) =>
                String(invoice?.invoiceStatus || "").toLowerCase() !== "cancelled",
        ) ||
        invoices[0] ||
        job
    );
};

const getInvoiceCount = (job) => {
    const invoices = getCustomerInvoices(job);
    return invoices.length || 1;
};

const getInvoiceReturnDocuments = (invoice) => {
    const candidates = [
        invoice?.returnInvoices,
        invoice?.return_invoices,
        invoice?.linkedReturnInvoices,
        invoice?.linked_return_invoices,
    ];

    return candidates.find(Array.isArray) || [];
};

const getFinanceRecordId = (job) =>
    getEntityId(
        job?.financeJobId ||
        job?.finance_job_id ||
        job?.jobFinanceId ||
        job?.job_finance_id ||
        job?._id ||
        job?.id,
    );

const getLinkedReturnInvoices = (job) => {
    const candidates = [
        job?.returnInvoices,
        job?.return_invoices,
        job?.linkedReturnInvoices,
        job?.linked_return_invoices,
        job?.previousReturns,
        job?.previous_returns,
    ];

    const directReturns = candidates.find(Array.isArray) || [];
    const invoiceReturns = getCustomerInvoices(job).flatMap(
        getInvoiceReturnDocuments,
    );

    return Array.from(
        new Map(
            [...directReturns, ...invoiceReturns].map((invoice, index) => [
                getEntityId(invoice?._id || invoice?.id) || `return-invoice-${index}`,
                invoice,
            ]),
        ).values(),
    );
};

const getReturnInvoiceAmount = (invoice) =>
    firstDefinedNumber(
        invoice?.returnedAmount,
        invoice?.returned_amount,
        invoice?.totalAmount,
        invoice?.total_amount,
        invoice?.invoiceSalesAmount,
        invoice?.invoice_sales_amount,
    );

const getReturnInvoiceStatus = (invoice) =>
    String(invoice?.returnStatus || invoice?.return_status || "Draft").trim();

const getReturnInvoiceDate = (invoice) => {
    const raw =
        invoice?.createdAt ||
        invoice?.created_at ||
        invoice?.invoiceDate ||
        invoice?.invoice_date;
    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getReturnSummary = (job) => {
    const returnInvoices = getLinkedReturnInvoices(job);
    const activeReturnInvoices = returnInvoices.filter(
        (invoice) =>
            !["cancelled", "not applicable"].includes(
                normalizeText(getReturnInvoiceStatus(invoice)),
            ),
    );
    const issuedReturnInvoices = activeReturnInvoices.filter(
        (invoice) => normalizeText(getReturnInvoiceStatus(invoice)) === "issued",
    );
    const latestReturnInvoice =
        [...returnInvoices].sort(
            (a, b) => getReturnInvoiceDate(b) - getReturnInvoiceDate(a),
        )[0] || null;
    const latestActiveReturnInvoice =
        [...activeReturnInvoices].sort(
            (a, b) => getReturnInvoiceDate(b) - getReturnInvoiceDate(a),
        )[0] || null;

    const calculatedReturnedAmount = issuedReturnInvoices.reduce(
        (sum, invoice) => sum + getReturnInvoiceAmount(invoice),
        0,
    );
    const calculatedReservedAmount = activeReturnInvoices.reduce(
        (sum, invoice) => sum + getReturnInvoiceAmount(invoice),
        0,
    );
    const returnedAmount = Math.max(
        toNumber(job?.returnedAmount ?? job?.returned_amount),
        calculatedReturnedAmount,
    );
    const reservedReturnAmount = Math.max(
        toNumber(job?.reservedReturnAmount ?? job?.reserved_return_amount),
        calculatedReservedAmount,
    );
    const grossAmount =
        [
            job?.grossInvoiceSalesAmount,
            job?.gross_invoice_sales_amount,
            job?.originalAmount,
            job?.original_amount,
            job?.originalInvoiceTotal,
            job?.original_invoice_total,
            job?.sellingRate,
            job?.selling_rate,
            job?.totalSellingRate,
            job?.total_selling_rate,
        ]
            .map(toNumber)
            .find((value) => value > 0) || getRevenue(job);
    const explicitStatus = normalizeText(job?.returnStatus || job?.return_status);
    const latestActiveStatus = normalizeText(
        getReturnInvoiceStatus(latestActiveReturnInvoice),
    );

    let status = "Not Returned";

    if (
        explicitStatus === "fully returned" ||
        (grossAmount > 0 && returnedAmount >= grossAmount - 0.01)
    ) {
        status = "Fully Returned";
    } else if (explicitStatus === "partially returned" || returnedAmount > 0) {
        status = "Partially Returned";
    } else if (latestActiveStatus === "approved") {
        status = "Return Approved";
    } else if (latestActiveStatus === "issued") {
        status = "Return Issued";
    } else if (latestActiveStatus === "draft" || reservedReturnAmount > 0) {
        status = "Return Draft";
    } else if (returnInvoices.length > 0) {
        status = "Return Cancelled";
    }

    return {
        status,
        returnedAmount,
        reservedReturnAmount,
        grossAmount,
        returnInvoices,
        activeReturnInvoices,
        latestReturnInvoice,
    };
};

// A return affects the real remaining selling rate only after it is issued.
// Draft and approved returns remain visible in the UI but do not change the
// customer invoice balance, matching the backend finance calculation.
const SETTLED_RETURN_STATUSES = new Set(["issued"]);

const getSettledReturnAmount = (job) =>
    getLinkedReturnInvoices(job)
        .filter((invoice) =>
            SETTLED_RETURN_STATUSES.has(
                normalizeText(getReturnInvoiceStatus(invoice)),
            ),
        )
        .reduce((sum, invoice) => sum + getReturnInvoiceAmount(invoice), 0);

const formatPdfMoney = (amount, currency) =>
    `${toNumber(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} ${normalizeCurrencyCode(currency)}`;

const pdfText = (value, fallback = "-") => {
    const text = String(value ?? "").trim();
    return text || fallback;
};

const loadInvoiceLetterhead = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            canvas.getContext("2d").drawImage(image, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        image.onerror = () => reject(new Error("Invoice letterhead could not be loaded."));
        image.src = url;
    });

const createNetSellingRatePdf = async (job) => {
    const invoice = getPrimaryInvoice(job);
    const currency = getCurrency(invoice || job);
    const summary = getReturnSummary(job);
    const explicitGross = firstPositiveNumber(
        invoice?.grossInvoiceSalesAmount,
        invoice?.gross_invoice_sales_amount,
        invoice?.originalAmount,
        invoice?.original_amount,
        invoice?.originalInvoiceTotal,
        invoice?.original_invoice_total,
        job?.grossInvoiceSalesAmount,
        job?.gross_invoice_sales_amount,
        job?.originalAmount,
        job?.original_amount,
        job?.originalInvoiceTotal,
        job?.original_invoice_total,
    );
    // Never use the aggregate returnedAmount field here: depending on the
    // endpoint it can contain draft/approved returns or an already-net value.
    // Summing only issued return documents prevents both double subtraction and
    // a pending return from changing the real invoice total.
    const settledReturnAmount = getSettledReturnAmount(job);
    // Do not use firstDefinedNumber here: an empty source represented by 0
    // would otherwise prevent the actual revenue value from being selected.
    const exVatGrossSellingRate = firstPositiveNumber(
        explicitGross,
        getRevenue(invoice),
        getRevenue(job),
        summary.grossAmount,
    );
    // When the invoice was cut with VAT the draft shows what the customer is
    // billed (VAT included); issued returns are VAT-inclusive documents too.
    const vatJob = hasVatIncluded(job);
    const grossSellingRate = vatJob
        ? getGrossBilledAmount(job, exVatGrossSellingRate)
        : exVatGrossSellingRate;
    const remainingSellingRate = Math.max(
        grossSellingRate - settledReturnAmount,
        0,
    );

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const PAGE_TOP = 45;
    const tableStyle = {
        theme: "grid",
        styles: { fontSize: 10, lineWidth: 0.7 },
        headStyles: {
            fillColor: [16, 28, 54],
            textColor: 255,
            fontStyle: "bold",
        },
    };

    let letterheadImage = null;
    try {
        letterheadImage = await loadInvoiceLetterhead("/images/letterhead.png");
    } catch (error) {
        console.warn(error.message);
    }

    const drawLetterhead = () => {
        if (letterheadImage) {
            doc.addImage(letterheadImage, "PNG", 0, 0, pageWidth, pageHeight);
        }
    };
    const ensurePage = (requiredHeight = 60) => {
        const lastY = doc.lastAutoTable?.finalY || PAGE_TOP;
        if (lastY + requiredHeight > pageHeight - 20) {
            doc.addPage();
            drawLetterhead();
            return PAGE_TOP;
        }
        return lastY + 10;
    };

    drawLetterhead();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("DRAFT INVOICE", pageWidth / 2, 35, { align: "center" });
    doc.setLineWidth(0.7);
    doc.line(50, 42, 160, 42);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
        `Date: ${new Date().toLocaleDateString()}`,
        pageWidth - 20,
        35,
        { align: "right" },
    );

    doc.setFont("helvetica", "bold");
    doc.text("Sender Information", pageWidth / 2, 50, { align: "center" });
    autoTable(doc, {
        startY: 55,
        margin: { top: PAGE_TOP },
        head: [["Field", "Details", "Field", "Details"]],
        body: [
            ["Company", "Filenders Lojistik ve Teknoloji A.S", "Phone", "+90 324 503 84 33"],
            ["Email", "accounting@filenders.com", "Payment Notice", "control.finance@filenders.com"],
            [
                "Address",
                "Mersin University Ciftlikkoy Campus Technopark, 1st Incubation Building 35B/B02, Yenisehir/Mersin",
                "TAX NO",
                "3871329803",
            ],
        ],
        ...tableStyle,
    });

    const customer = invoice || job;
    const customerInformationY = ensurePage(70);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Information", pageWidth / 2, customerInformationY, {
        align: "center",
    });
    autoTable(doc, {
        startY: customerInformationY + 5,
        margin: { top: PAGE_TOP },
        head: [["Field", "Details", "Field", "Details"]],
        body: [
            [
                "Invoice No",
                pdfText(invoice?.invoiceNo || invoice?.invoice_no || job?.invoiceNo),
                "Shipment / Job No",
                pdfText(job?.jobNo || job?.job_no || job?.shipmentNo),
            ],
            [
                "Customer",
                pdfText(getCustomerName(customer)),
                "Email",
                pdfText(customer?.email || customer?.customer?.email),
            ],
            [
                "Tax ID",
                pdfText(customer?.taxID || customer?.taxId || customer?.customer?.taxID),
                "Invoice Address",
                pdfText(customer?.billingAddress || customer?.customer?.billingAddress || customer?.customer?.address),
            ],
        ],
        ...tableStyle,
    });

    const invoiceItemsY = ensurePage(80);
    autoTable(doc, {
        startY: invoiceItemsY,
        margin: { top: PAGE_TOP },
        head: [["#", "Invoice Item", "Description", "Currency", "Amount"]],
        body: [
            [
                1,
                "Freight Charge",
                vatJob ? "Actual Selling Rate (incl. VAT)" : "Actual Selling Rate",
                currency,
                formatPdfMoney(grossSellingRate, currency),
            ],
            ...(settledReturnAmount > 0
                ? [[2, "Issued Return", "Issued Return (-)", currency, `-${formatPdfMoney(settledReturnAmount, currency)}`]]
                : []),
            [
                settledReturnAmount > 0 ? 3 : 2,
                "Net Freight Charge",
                settledReturnAmount > 0
                    ? "Remaining Invoice Amount"
                    : vatJob
                        ? "Actual Selling Rate (incl. VAT)"
                        : "Actual Selling Rate",
                currency,
                formatPdfMoney(remainingSellingRate, currency),
            ],
        ],
        ...tableStyle,
        columnStyles: {
            0: { cellWidth: 12, halign: "center" },
            3: { cellWidth: 25, halign: "center" },
            4: { halign: "right", fontStyle: "bold" },
        },
        styles: { ...tableStyle.styles, fontSize: 9, halign: "center" },
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(
        `Total Amount: ${formatPdfMoney(remainingSellingRate, currency)}`,
        pageWidth - 20,
        doc.lastAutoTable.finalY + 10,
        { align: "right" },
    );

    if (vatJob) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(
            `Includes VAT: ${formatPdfMoney(getVatAmount(job), currency)}`,
            pageWidth - 20,
            doc.lastAutoTable.finalY + 16,
            { align: "right" },
        );
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
        settledReturnAmount > 0
            ? "This draft reflects the remaining balance after issued returns."
            : "No issued return exists; this draft reflects the actual selling rate.",
        14,
        doc.lastAutoTable.finalY + 25,
    );

    doc.addPage();
    drawLetterhead();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("BANK DETAILS", pageWidth / 2, PAGE_TOP - 10, { align: "center" });
    autoTable(doc, {
        startY: PAGE_TOP,
        margin: { top: PAGE_TOP },
        head: [["BANK", "CURRENCY", "IBAN", "BRANCH", "SWIFT"]],
        body: bankRowsForInvoice([
            ["T.GARANTI BANKASI", "USD", "TR20 0006 2001 1730 0009 0929 99", "MERSIN SANAYI", "TGBATRISXXX"],
            ["T.GARANTI BANKASI", "EURO", "TR90 0006 2001 1730 0009 0930 00", "MERSIN SANAYI", "TGBATRISXXX"],
            ["T.GARANTI BANKASI", "GBP", "TR20 0006 2001 1730 0009 0915 44", "MERSIN SANAYI", "TGBATRISXXX"],
            ["T.GARANTI BANKASI", "TRY", "TR94 0006 2001 1730 0006 2962 24", "MERSIN SANAYI", ""],
            ["T.IS BANKASI MERSIN", "USD", "TR34 0006 4000 0026 6030 1533 33", "KUCUK SANAYI", "ISBKTRIS"],
            ["T.IS BANKASI MERSIN", "EURO", "TR72 0006 4000 0026 6030 1533 28", "KUCUK SANAYI", "ISBKTRIS"],
            ["T.IS BANKASI MERSIN", "TRY", "TR98 0006 4000 0016 6030 3901 27", "KUCUK SANAYI", ""],
            ["ZIRAAT BANKASI MERSIN", "USD", "TR80 0001 0019 6097 8746 9350 02", "INONU BULVARI", "TCZBTR2A"],
            ["ZIRAAT BANKASI MERSIN", "EURO", "TR53 0001 0019 6097 8746 9350 03", "INONU BULVARI", "TCZBTR2A"],
            ["ZIRAAT BANKASI MERSIN", "TRY", "TR10 0001 0019 6097 8746 9350 01", "INONU BULVARI", ""],
            ["VAKIFBANK", "USD", "TR72 0001 5001 5804 8024 3766 06", "POZCU MERSIN", ""],
            ["VAKIFBANK", "EURO", "TR62 0001 5001 5804 8024 3766 89", "POZCU MERSIN", ""],
            ["VAKIFBANK", "TRY", "TR05 0001 5001 5800 7333 9760 46", "POZCU MERSIN", ""],
        ], { currency, foreign: isForeignCountry(await resolveCustomerCountry(invoice, job)) }),
        ...tableStyle,
        styles: { ...tableStyle.styles, fontSize: 9 },
    });
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        theme: "grid",
        head: [["Payment Terms and Notification"]],
        body: [["Payments must be made in full within the payment term specified on this invoice.\nThe due date is calculated as of the invoice date and is explicitly stated on the invoice.\n\nUpon completion of payment, the SWIFT transfer confirmation must be sent to:\n• control.finance@filenders.com\n• accounting@filenders.com"]],
        styles: { fontSize: 9, lineWidth: 0.4, cellPadding: 7 },
        headStyles: { fillColor: [16, 28, 54], textColor: 255, halign: "center" },
    });

    return doc;
};

const RETURN_STATUS_STYLES = {
    "Not Returned": {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    "Return Issued": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Partially Returned": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Fully Returned": {
        color: "#027A48",
        background: "#ECFDF3",
        border: "#ABEFC6",
    },
    "Return Cancelled": {
        color: "#B42318",
        background: "#FEF3F2",
        border: "#FECDCA",
    },
};

// `convert(amount, fromCurrency, toCurrency)` is optional. A job's revenue
// currency and its cost currency routinely differ (carrier cost quoted in USD
// against a customer billed in EUR/TRY), and subtracting the raw numbers in
// that case produces a meaningless profit -- and therefore a meaningless
// margin ratio. Passing a converter (see FinanceJobs' local `convertCurrency`,
// or the `getConvertedFinancials` helper that wraps it) normalizes the cost
// into the revenue currency first; without one this falls back to the old raw
// subtraction, which is only correct when both currencies already match.
// Returns null when a rate is missing so callers can show "Rate unavailable"
// instead of a wrong number.
const getProfit = (job, convert) => {
    const revenue = getRevenue(job);
    const cost = getCost(job);

    if (typeof convert !== "function") return revenue - cost;

    const revenueCurrency = normalizeCurrencyCode(getCurrency(job));
    const costCurrency = normalizeCurrencyCode(getCostCurrency(job));

    if (revenueCurrency === costCurrency) return revenue - cost;

    const convertedCost = convert(cost, costCurrency, revenueCurrency);
    if (convertedCost === null || convertedCost === undefined) return null;

    return revenue - convertedCost;
};

const getMargin = (job, convert) => {
    const revenue = getRevenue(job);
    if (!revenue) return 0;

    const profit = getProfit(job, convert);
    return profit === null ? null : (profit / revenue) * 100;
};

const getShipmentNo = (job) =>
    getValue(job, "jobNo", "job_no") ||
    job?.shipmentNo ||
    job?.shipment_no ||
    job?.invoiceNo ||
    job?.invoice_no ||
    "N/A";

const getCustomerName = (job) =>
    getValue(job, "customerName", "customer_name", job?.customer || "N/A");

const getSupplierName = (job) =>
    job?.supplierName ||
    job?.supplier_name ||
    job?.serviceProviderName ||
    job?.service_provider_name ||
    job?.linehaulServiceProvider ||
    job?.supplier?.companyName ||
    job?.serviceProvider?.companyName ||
    "N/A";

const getDateValue = (job) => {
    const raw =
        job?.invoiceDate ||
        job?.invoice_date ||
        job?.customerInvoiceDate ||
        job?.supplierInvoiceDate ||
        job?.transactionDate ||
        job?.postingDate ||
        job?.createdAt ||
        job?.created_at ||
        job?.updatedAt ||
        job?.updated_at;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");

const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    }

    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};

const getDirectCountryValues = (job) => {
    const related = [
        job,
        job?.customer,
        job?.serviceProvider,
        job?.supplier,
        job?.globalAgent,
        job?.selectedBookingAgent,
        job?.selectedOriginAgent,
        job?.selectedDestinationAgent,
        job?.selectedNotifyAgent,
    ].filter((item) => item && typeof item === "object");

    return related
        .flatMap((item) => [
            item?.customerCountry,
            item?.serviceProviderCountry,
            item?.globalAgentCountry,
            ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
        ])
        .filter(Boolean);
};

const getJobPartyKeys = (job) =>
    [
        job?.customerId,
        job?.customer,
        job?.customerName,
        job?.customer_name,
        job?.serviceProviderId,
        job?.serviceProvider,
        job?.serviceProviderName,
        job?.supplierId,
        job?.supplier,
        job?.supplierName,
        job?.supplier_name,
        job?.globalAgentId,
        job?.globalAgent,
        job?.globalAgentName,
        job?.selectedBookingAgent,
        job?.selectedOriginAgent,
        job?.selectedDestinationAgent,
        job?.selectedNotifyAgent,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);

const getGeographicScope = (job, lookup = new Map()) => {
    const masterCountries = getJobPartyKeys(job).flatMap(
        (key) => lookup.get(key) || [],
    );
    const domestic = [...getDirectCountryValues(job), ...masterCountries].some(
        (country) => ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    );
    return domestic ? "DOMESTIC" : "INTERNATIONAL";
};

const getGeographicLabel = (job, lookup) =>
    getGeographicScope(job, lookup) === "DOMESTIC" ? "Domestic" : "International";

// Currencies offered by the "Display Currency" selector, which only changes
// which currency AGGREGATED figures are shown in. It is deliberately separate
// from `currencyFilter` (the record filter that narrows the table to jobs
// billed in a given currency) and never filters anything itself.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

// Converts EVERY currency bucket of a `{currency: amount}` map into
// `targetCurrency` (even a single-entry bucket) and sums them. For numeric
// comparisons like profit/margin ratios, where both sides must land in the
// SAME currency even when each side is individually single-currency (e.g.
// revenue all in EUR against cost all in TRY: neither bucket alone is
// "mixed", but subtracting them without converting is still wrong). Returns
// null if a rate is missing so callers can fall back to a raw figure.
const sumConvertedTo = (byCurrency, convert, targetCurrency = "EUR") => {
    const entries = Object.entries(byCurrency).filter(([, amount]) => amount !== 0);
    if (entries.length === 0) return 0;
    if (typeof convert !== "function") return null;

    let total = 0;

    for (const [currency, amount] of entries) {
        const converted = convert(amount, currency, targetCurrency);
        if (converted === null || converted === undefined) return null;
        total += converted;
    }

    return total;
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(Number(value || 0));

const normalizeText = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[ıİ]/g, "i");

const getInvoiceOrigin = (job) =>
    normalizeText(job?.invoiceOrigin || job?.invoice_origin || "tms") === "manual"
        ? "manual"
        : "tms";

// A finance-job/customer-invoice id is never interchangeable with a manual
// invoice id.  This explicit reference is the only safe way to call the
// manual-invoice API from a mixed finance list.
const getDedicatedManualInvoiceId = (record) =>
    getEntityId(record?.manualInvoiceId || record?.manual_invoice_id);

const getTransportMode = (job) => {
    if (getInvoiceOrigin(job) === "manual") {
        return "manual";
    }

    const candidates = [
        job?.sourceType,
        job?.source_type,
        job?.transportMode,
        job?.transport_mode,
        job?.shipmentMode,
        job?.shipment_mode,
        job?.serviceType,
        job?.service_type,
        job?.tmsSource,
        job?.tms_source,
        job?.module,
        job?.source,
        getShipmentNo(job),
    ]
        .map(normalizeText)
        .join(" ");

    if (
        candidates.includes("ocean") ||
        candidates.includes("sea") ||
        candidates.includes("fcl") ||
        candidates.includes("lcl") ||
        candidates.includes("ohfl") ||
        candidates.includes("ombl")
    ) {
        return "ocean";
    }

    if (
        candidates.includes("air") ||
        candidates.includes("flight") ||
        candidates.includes("ahfl") ||
        candidates.includes("ambl")
    ) {
        return "air";
    }

    return "road";
};

const getShipmentType = (job) => {
    const raw =
        job?.shipmentType ||
        job?.shipment_type ||
        job?.transportType ||
        job?.transport_type ||
        job?.oceanTransportType ||
        job?.ocean_transport_type ||
        job?.airTransportType ||
        job?.air_transport_type ||
        job?.serviceType ||
        job?.service_type ||
        "General";

    const normalized = String(raw).trim();
    return normalized || "General";
};

const MODE_CONFIG = {
    ocean: {
        label: "Ocean",
        color: "#1570EF",
        light: "#EFF8FF",
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    road: {
        label: "Road",
        color: "#B54708",
        light: "#FFFAEB",
        border: "#FEDF89",
        icon: LocalShipping,
    },
    air: {
        label: "Air",
        color: "#6941C6",
        light: "#F4F3FF",
        border: "#D9D6FE",
        icon: Flight,
    },
    manual: {
        label: "Manual",
        color: "#475467",
        light: "#F2F4F7",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
};

// `formatAmount(value, currency)` lets the page render these already-blended
// mode totals in the user's chosen display currency (see the "Display
// Currency" selector); it defaults to plain formatting in `currency`.
const TransportModeCard = ({
    mode,
    data,
    totalJobs,
    currency = "EUR",
    formatAmount = formatCompact,
}) => {
    const config = MODE_CONFIG[mode];
    const Icon = config.icon;
    const share = totalJobs ? (data.jobs / totalJobs) * 100 : 0;

    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.4}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: COLORS.subtext, letterSpacing: ".08em" }}
                        >
                            {`${financeText(config.label).toUpperCase()} ${financeText("OPERATIONS")}`}
                        </MDTypography>
                        <MDTypography
                            variant="h4"
                            fontWeight="bold"
                            mt={0.8}
                            sx={{ color: COLORS.text }}
                        >
                            {formatNumber(data.jobs)}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Finance jobs")} · {share.toFixed(1)}% {financeText("share")}
                        </MDTypography>
                    </MDBox>

                    <MDBox
                        width={46}
                        height={46}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            color: config.color,
                            background: config.light,
                            border: `1px solid ${config.border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                </MDBox>

                <MDBox mt={2}>
                    <MDBox display="flex" justifyContent="space-between" mb={0.6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>{financeText("Revenue")}</MDTypography>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: config.color }}
                        >
                            {formatAmount(data.revenue, currency)}
                        </MDTypography>
                    </MDBox>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(
                            100,
                            Math.max(5, data.margin > 0 ? data.margin : 5),
                        )}
                        sx={{
                            height: 7,
                            borderRadius: "999px",
                            backgroundColor: "#EEF2F6",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: "999px",
                                backgroundColor: config.color,
                            },
                        }}
                    />
                </MDBox>

                <Grid container spacing={1.2} mt={0.8}>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >{financeText("Cost")}</MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.red }}
                        >
                            {formatAmount(data.cost, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >{financeText("Profit")}</MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: data.profit >= 0 ? COLORS.green : COLORS.red }}
                        >
                            {formatAmount(data.profit, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={4}>
                        <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: COLORS.subtext }}
                        >{financeText("Margin")}</MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.text }}
                        >
                            {formatPercent(data.margin)}
                        </MDTypography>
                    </Grid>
                </Grid>
            </MDBox>
        </Card>
    );
};

const ModeComparisonChart = ({
    summary,
    currency = "EUR",
    formatAmount = formatCompact,
}) => {
    const maxRevenue = Math.max(
        ...Object.values(summary).map((item) => item.revenue),
        1,
    );

    return (
        <MDBox>
            {Object.entries(summary).map(([mode, item]) => {
                const config = MODE_CONFIG[mode];
                const width = (item.revenue / maxRevenue) * 100;

                return (
                    <MDBox key={mode} mb={2.2}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.8}
                        >
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDBox
                                    width={10}
                                    height={10}
                                    borderRadius="50%"
                                    sx={{ background: config.color }}
                                />
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText(config.label)}
                                </MDTypography>
                            </MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: config.color }}
                            >
                                {formatAmount(item.revenue, currency)}
                            </MDTypography>
                        </MDBox>
                        <MDBox
                            height={12}
                            borderRadius="999px"
                            sx={{ background: "#EEF2F6", overflow: "hidden" }}
                        >
                            <MDBox
                                height="100%"
                                borderRadius="999px"
                                sx={{
                                    width: `${Math.max(width, item.jobs ? 4 : 0)}%`,
                                    background: config.color,
                                }}
                            />
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
};

const FinanceJobs = () => {
    const navigate = useNavigate();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [jobs, setJobs] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [search, setSearch] = useState("");
    const [financeStatus, setFinanceStatus] = useState("all");
    const [collectionStatus, setCollectionStatus] = useState("all");
    const [creditRisk, setCreditRisk] = useState("all");
    const [customerInvoiceStatus, setCustomerInvoiceStatus] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);
    // Display-only currency for aggregated figures. Intentionally separate
    // from `currencyFilter` above, which filters WHICH records are listed by
    // their own billing currency and is left untouched.
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const token = localStorage.getItem("token");
    const reportingCurrency =
        currencyFilter === "all" ? "EUR" : normalizeCurrencyCode(currencyFilter);

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`${path} fetch failed`);
            }

            const data = await response.json();
            const candidates = [
                data,
                data?.data,
                data?.jobs,
                data?.invoices,
                data?.results,
                data?.docs,
                data?.items,
                data?.records,
                data?.data?.data,
                data?.data?.jobs,
                data?.data?.invoices,
                data?.data?.results,
                data?.data?.records,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = normalizeCurrencyCode(fromCurrency);
            const to = normalizeCurrencyCode(toCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const getConvertedFinancials = useCallback(
        (job, targetCurrency = reportingCurrency) => {
            const revenue = convertCurrency(
                getRevenue(job),
                getCurrency(job),
                targetCurrency,
            );
            const cost = convertCurrency(
                getCost(job),
                getCostCurrency(job),
                targetCurrency,
            );

            if (revenue === null || cost === null) {
                return { revenue: null, cost: null, profit: null, margin: null };
            }

            const profit = revenue - cost;
            return {
                revenue,
                cost,
                profit,
                margin: revenue > 0 ? (profit / revenue) * 100 : 0,
            };
        },
        [convertCurrency, reportingCurrency],
    );

    // Display layer for the "Display Currency" selector. Every aggregated /
    // blended figure below (KPI hero, transport-mode cards, mode comparison
    // chart, footer chips, CSV totals) is computed first in `reportingCurrency`
    // and then converted here for rendering only, so the underlying math and
    // per-record row amounts are untouched. Falls back to the source currency
    // -- correctly labelled, never mislabelled as the selected one -- while
    // the exchange rates are still loading or a rate is missing.
    const toDisplayAmount = useCallback(
        (amount, fromCurrency = reportingCurrency) => {
            const source = normalizeCurrencyCode(fromCurrency);
            const converted = convertCurrency(amount, source, selectedCurrency);

            return converted === null || converted === undefined
                ? { amount: Number(amount) || 0, currency: source }
                : {
                    amount: converted,
                    currency: normalizeCurrencyCode(selectedCurrency),
                };
        },
        [convertCurrency, reportingCurrency, selectedCurrency],
    );

    const formatDisplayCompact = useCallback(
        (amount, fromCurrency = reportingCurrency) => {
            const shown = toDisplayAmount(amount, fromCurrency);
            return formatCompact(shown.amount, shown.currency);
        },
        [toDisplayAmount, reportingCurrency],
    );

    const fetchFinanceJobs = useCallback(async () => {
        try {
            setLoading(true);

            const [financeData, returnInvoices] = await Promise.all([
                fetchWithAuth("/finance/jobs"),
                fetchWithAuth("/finance/return-invoices").catch((error) => {
                    console.warn("Return invoices could not be loaded:", error);
                    return [];
                }),
            ]);

            const returnsByOriginalInvoiceId = new Map();

            returnInvoices.forEach((returnInvoice) => {
                const originalInvoiceId = getEntityId(
                    returnInvoice?.originalInvoiceId ||
                    returnInvoice?.original_invoice_id,
                );

                if (!originalInvoiceId) return;

                returnsByOriginalInvoiceId.set(originalInvoiceId, [
                    ...(returnsByOriginalInvoiceId.get(originalInvoiceId) || []),
                    returnInvoice,
                ]);
            });

            const groupedJobs = financeData.map((job) => {
                const customerInvoices = getCustomerInvoices(job).map((invoice) => {
                    const invoiceId = getEntityId(invoice?._id || invoice?.id);
                    const linkedReturns = [
                        ...getInvoiceReturnDocuments(invoice),
                        ...(returnsByOriginalInvoiceId.get(invoiceId) || []),
                    ];
                    const uniqueReturns = Array.from(
                        new Map(
                            linkedReturns.map((returnInvoice, index) => [
                                getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                `${invoiceId}-return-${index}`,
                                returnInvoice,
                            ]),
                        ).values(),
                    ).sort((a, b) => getReturnInvoiceDate(b) - getReturnInvoiceDate(a));

                    return { ...invoice, returnInvoices: uniqueReturns };
                });

                return {
                    ...job,
                    financeJobId: getFinanceRecordId(job),
                    customerInvoices,
                };
            });

            setJobs(groupedJobs);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Finance jobs fetch error:", error);

            setJobs([]);

            appAlert(financeText("Finance jobs could not be loaded."));
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };

        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchFinanceJobs();
        fetchBusinessPartners();
    }, [fetchFinanceJobs, fetchBusinessPartners]);

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            const keys = [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean);
            keys.forEach((key) =>
                lookup.set(key, [...(lookup.get(key) || []), ...countries]),
            );
        });
        return lookup;
    }, [businessPartners]);

    const statusOptions = useMemo(() => {
        const pickUnique = (fieldCamel, fieldSnake, fallback) => {
            const values = jobs
                .map((job) =>
                    String(getValue(job, fieldCamel, fieldSnake, fallback)).trim(),
                )
                .filter(Boolean);

            return ["all", ...Array.from(new Set(values))];
        };

        return {
            finance: pickUnique("financeStatus", "finance_status", "Finance Open"),
            collection: pickUnique(
                "collectionStatus",
                "collection_status",
                "Pending",
            ),
            customerInvoice: pickUnique(
                "customerInvoiceStatus",
                "customer_invoice_status",
                "Issued",
            ),
        };
    }, [jobs]);

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(new Set(jobs.map((job) => getReturnSummary(job).status))),
        ],
        [jobs],
    );

    const partyOptions = useMemo(() => {
        const unique = new Map();

        jobs.forEach((job) => {
            const customerName = getCustomerName(job);
            const supplierName = getSupplierName(job);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [jobs]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(jobs.map(getTransportMode))).sort(),
        [jobs],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(jobs.map((job) => String(getCurrency(job)).toUpperCase())),
            ).sort(),
        [jobs],
    );

    const filteredJobs = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return jobs.filter((job) => {
            const jobNo = String(getShipmentNo(job)).toLowerCase();
            const customer = String(getCustomerName(job)).toLowerCase();
            const email = String(job?.email || "").toLowerCase();
            const createdBy = String(job?.createdBy || "").toLowerCase();
            const supplier = String(getSupplierName(job)).toLowerCase();
            const jobDate = getDateValue(job);

            const currentFinanceStatus = String(
                getValue(job, "financeStatus", "finance_status", "Finance Open"),
            ).toLowerCase();

            const currentCollectionStatus = String(
                getValue(job, "collectionStatus", "collection_status", "Pending"),
            ).toLowerCase();

            const currentCustomerInvoiceStatus = String(
                getValue(
                    job,
                    "customerInvoiceStatus",
                    "customer_invoice_status",
                    "Issued",
                ),
            ).toLowerCase();
            const currentReturnStatus = normalizeText(getReturnSummary(job).status);

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                customer.includes(searchText) ||
                supplier.includes(searchText) ||
                email.includes(searchText) ||
                createdBy.includes(searchText);

            const matchesStartDate =
                !rangeStart || (jobDate && jobDate >= rangeStart);
            const matchesEndDate = !rangeEnd || (jobDate && jobDate <= rangeEnd);

            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(job) === selectedParty.name
                    : getSupplierName(job) === selectedParty.name);

            const matchesTransportMode =
                transportMode === "all" || getTransportMode(job) === transportMode;

            const matchesInvoiceOrigin =
                invoiceOrigin === "all" || getInvoiceOrigin(job) === invoiceOrigin;

            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(job)).toUpperCase() === currencyFilter;

            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(job, partyCountryLookup) === geographicScope;

            const matchesFinance =
                financeStatus === "all" ||
                currentFinanceStatus === financeStatus.toLowerCase();

            const matchesCollection =
                collectionStatus === "all" ||
                currentCollectionStatus === collectionStatus.toLowerCase();

            const matchesCreditRisk =
                creditRisk === "all" || getCollectionRisk(job).status === creditRisk;

            const matchesCustomerInvoice =
                customerInvoiceStatus === "all" ||
                currentCustomerInvoiceStatus === customerInvoiceStatus.toLowerCase();

            const matchesReturnStatus =
                returnStatus === "all" ||
                currentReturnStatus === normalizeText(returnStatus);

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesInvoiceOrigin &&
                matchesCurrency &&
                matchesGeographicScope &&
                matchesFinance &&
                matchesCollection &&
                matchesCreditRisk &&
                matchesCustomerInvoice &&
                matchesReturnStatus
            );
        });
    }, [
        jobs,
        search,
        financeStatus,
        collectionStatus,
        creditRisk,
        customerInvoiceStatus,
        returnStatus,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        invoiceOrigin,
        currencyFilter,
        geographicScope,
        partyCountryLookup,
    ]);

    const stats = useMemo(() => {
        // getConvertedFinancials returns null revenue/cost when the live
        // exchange-rate API is unreachable and the job's currency differs
        // from the reporting currency. Coalescing that straight to 0 (the
        // old behavior) silently understated these KPI totals with no
        // indication anything was missing, even though the same jobs'
        // table rows correctly show "Rate unavailable". Track whether any
        // job was skipped so the total can be flagged instead.
        let ratesIncomplete = false;

        const totalRevenue = filteredJobs.reduce((sum, job) => {
            const { revenue } = getConvertedFinancials(job);
            if (revenue === null) ratesIncomplete = true;
            return sum + (revenue ?? 0);
        }, 0);

        const totalCost = filteredJobs.reduce((sum, job) => {
            const { cost } = getConvertedFinancials(job);
            if (cost === null) ratesIncomplete = true;
            return sum + (cost ?? 0);
        }, 0);

        const collected = filteredJobs.reduce(
            (sum, job) =>
                sum +
                (convertCurrency(
                    getPaidAmount(job),
                    getCurrency(job),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const profit = totalRevenue - totalCost;
        const margin = totalRevenue ? (profit / totalRevenue) * 100 : 0;
        const collectionRate = totalRevenue ? (collected / totalRevenue) * 100 : 0;

        const highRiskExposure = filteredJobs.reduce((sum, job) => {
            if (
                !["doubtful", "bad_debt", "written_off"].includes(
                    getCollectionRisk(job).status,
                )
            ) {
                return sum;
            }

            const balance = Math.max(
                getBilledAmount(job, getRevenue(job)) - getPaidAmount(job),
                0,
            );
            return (
                sum +
                (convertCurrency(balance, getCurrency(job), reportingCurrency) ?? 0)
            );
        }, 0);

        const highRiskCount = filteredJobs.filter((job) =>
            ["doubtful", "bad_debt", "written_off"].includes(
                getCollectionRisk(job).status,
            ),
        ).length;

        const waitingSupplierInvoice = filteredJobs.filter((job) =>
            String(
                getValue(job, "supplierInvoiceStatus", "supplier_invoice_status", ""),
            )
                .toLowerCase()
                .includes("waiting"),
        ).length;

        const waitingCustomerInvoice = filteredJobs.filter((job) =>
            String(
                getValue(job, "customerInvoiceStatus", "customer_invoice_status", ""),
            )
                .toLowerCase()
                .includes("waiting"),
        ).length;

        return {
            totalRevenue,
            totalCost,
            collected,
            profit,
            margin,
            collectionRate,
            highRiskExposure,
            highRiskCount,
            openJobs: filteredJobs.length,
            waitingSupplierInvoice,
            waitingCustomerInvoice,
            ratesIncomplete,
        };
    }, [
        filteredJobs,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const transportSummary = useMemo(() => {
        const initial = {
            ocean: { jobs: 0, revenue: 0, cost: 0, profit: 0, margin: 0, types: {} },
            road: { jobs: 0, revenue: 0, cost: 0, profit: 0, margin: 0, types: {} },
            air: { jobs: 0, revenue: 0, cost: 0, profit: 0, margin: 0, types: {} },
            manual: { jobs: 0, revenue: 0, cost: 0, profit: 0, margin: 0, types: {} },
        };

        filteredJobs.forEach((job) => {
            const mode = getTransportMode(job);
            const shipmentType = getShipmentType(job);
            const financials = getConvertedFinancials(job);
            const revenue = financials.revenue ?? 0;
            const cost = financials.cost ?? 0;

            initial[mode].jobs += 1;
            initial[mode].revenue += revenue;
            initial[mode].cost += cost;
            initial[mode].profit += revenue - cost;
            initial[mode].types[shipmentType] =
                (initial[mode].types[shipmentType] || 0) + 1;
        });

        Object.values(initial).forEach((item) => {
            item.margin = item.revenue ? (item.profit / item.revenue) * 100 : 0;
        });

        return initial;
    }, [filteredJobs, getConvertedFinancials]);

    const shipmentTypeSummary = useMemo(() => {
        const counts = {};

        filteredJobs.forEach((job) => {
            const type = getShipmentType(job);
            counts[type] = (counts[type] || 0) + 1;
        });

        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);
    }, [filteredJobs]);

    const resolveFinanceRecord = async (job) => {
        const manualInvoiceId = getDedicatedManualInvoiceId(job);
        const isManualInvoice =
            getInvoiceOrigin(job) === "manual" && Boolean(manualInvoiceId);
        const recordIds = [
            job?.financeJobId,
            job?.finance_job_id,
            job?.jobFinanceId,
            job?.job_finance_id,
            job?._id,
            job?.id,
        ].filter(Boolean);
        const uniqueIds = [...new Set(recordIds.map(String))];

        const detailPaths = isManualInvoice
            ? [`/finance/manual-invoices/${encodeURIComponent(manualInvoiceId)}`]
            : uniqueIds.flatMap((recordId) => [
                `/invoice/${recordId}`,
                `/finance/jobs/${recordId}`,
                `/finance/customer-invoices/${recordId}`,
            ]);

        for (const path of detailPaths) {
            try {
                const response = await fetch(`${API_URL}${path}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) continue;

                const payload = await response.json();
                const detail =
                    payload?.data?.invoice ||
                    payload?.data?.job ||
                    payload?.data?.data ||
                    payload?.invoice ||
                    payload?.job ||
                    payload?.data ||
                    payload;

                if (detail && typeof detail === "object" && !Array.isArray(detail)) {
                    return {
                        ...job,
                        ...detail,
                        invoiceItems:
                            detail?.invoiceItems ||
                            detail?.invoice_items ||
                            job?.invoiceItems ||
                            job?.invoice_items ||
                            [],
                    };
                }
            } catch (error) {
                console.warn(`${path} finance detail lookup failed`, error);
            }
        }

        return job;
    };

    const openFinanceDetail = (job) => {
        const financeJobId = getFinanceRecordId(job);

        if (!financeJobId) {
            appAlert(financeText("Finance Job ID not found!"));
            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job,
                financeJobId,
                customerInvoices: getCustomerInvoices(job),
            },
        });
    };

    const openInvoiceEditor = async (job) => {
        const customerInvoices = getCustomerInvoices(job);

        if (customerInvoices.length > 1) {
            openFinanceDetail(job);
            return;
        }

        const resolvedJob = await resolveFinanceRecord(
            customerInvoices[0] || getPrimaryInvoice(job),
        );
        const manualInvoiceId = getDedicatedManualInvoiceId(resolvedJob);

        if (getInvoiceOrigin(resolvedJob) === "manual" && manualInvoiceId) {
            navigate(`/invoice/finance/manual-invoices/${manualInvoiceId}/edit`, {
                state: {
                    invoice: resolvedJob,
                    invoiceId: manualInvoiceId,
                    editMode: true,
                },
            });
            return;
        }

        const shipmentId =
            getEntityId(resolvedJob?.shipmentId) ||
            getEntityId(resolvedJob?.shipment_id) ||
            getEntityId(resolvedJob?.shipment) ||
            getEntityId(resolvedJob?.shipmentObjectId) ||
            getEntityId(resolvedJob?.shipment_object_id);

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found!"));
            return;
        }

        const sellingCurrency = getCurrency(resolvedJob);
        const costCurrency = getCostCurrency(resolvedJob);
        const sourceItems =
            [
                resolvedJob?.invoiceItems,
                resolvedJob?.invoice_items,
                resolvedJob?.cargoDetails,
                resolvedJob?.cargo_details,
            ].find(Array.isArray) || [];

        const normalizedItems = sourceItems.map((item) => {
            const rawItem = item?.attributes || item;
            const unitQuantity =
                firstDefinedNumber(
                    rawItem?.unitQuantity,
                    rawItem?.unit_quantity,
                    rawItem?.quantity,
                    rawItem?.qty,
                ) || 1;
            const explicitUnitPrice = firstDefinedNumber(
                rawItem?.unitPrice,
                rawItem?.unit_price,
                rawItem?.sellingRate,
                rawItem?.selling_rate,
                rawItem?.salesAmount,
                rawItem?.price,
            );
            const amountBasedUnitPrice =
                firstDefinedNumber(rawItem?.amount) / unitQuantity;
            const unitPrice =
                explicitUnitPrice > 0 ? explicitUnitPrice : amountBasedUnitPrice;

            return {
                ...rawItem,
                commodity:
                    rawItem?.commodity ||
                    rawItem?.invoiceItem ||
                    rawItem?.invoice_item ||
                    rawItem?.label ||
                    "",
                description: rawItem?.description || "",
                totalCost: firstDefinedNumber(
                    rawItem?.totalCost,
                    rawItem?.total_cost,
                    rawItem?.cost,
                    rawItem?.unitCost,
                    rawItem?.unit_cost,
                ),
                costCurrency: normalizeCurrencyCode(
                    rawItem?.costCurrency || rawItem?.cost_currency || costCurrency,
                ),
                unitPrice,
                sellingRate: unitPrice,
                sellingRateCurrency: normalizeCurrencyCode(
                    rawItem?.sellingRateCurrency ||
                    rawItem?.selling_rate_currency ||
                    rawItem?.currency ||
                    sellingCurrency,
                ),
                unitQuantity,
                amount: unitPrice * unitQuantity,
            };
        });

        const invoiceItems = normalizedItems.length
            ? normalizedItems
            : [
                {
                    commodity: "Freight Charge",
                    description: "Shipment freight service",
                    totalCost: getCost(resolvedJob),
                    costCurrency,
                    unitPrice: getRevenue(resolvedJob),
                    sellingRate: getRevenue(resolvedJob),
                    sellingRateCurrency: sellingCurrency,
                    unitQuantity: 1,
                    amount: getRevenue(resolvedJob),
                },
            ];
        const convertedProfit = getConvertedFinancials(
            resolvedJob,
            sellingCurrency,
        ).profit;
        const sourceType = getTransportMode(resolvedJob);

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                editMode: true,
                invoiceId:
                    resolvedJob?._id ||
                    resolvedJob?.id ||
                    resolvedJob?.invoiceId ||
                    resolvedJob?.invoice_id ||
                    null,
                financeJobId:
                    resolvedJob?.financeJobId ||
                    resolvedJob?.finance_job_id ||
                    resolvedJob?.jobFinanceId ||
                    resolvedJob?.job_finance_id ||
                    null,
                shipments: [
                    {
                        ...resolvedJob,
                        _id: shipmentId,
                        shipmentId,
                        shipmentNo: String(
                            resolvedJob?.shipmentNo || resolvedJob?.jobNo || "",
                        ).replace(/^(OHFL|RHFL|AHFL)/, ""),
                        customerName: getCustomerName(resolvedJob),
                        customer: getCustomerName(resolvedJob),
                        invoiceItems,
                        cargoDetails: invoiceItems,
                        sellingRateCurrency: sellingCurrency,
                        costCurrency,
                        totalCostCurrency: costCurrency,
                        invoiceSalesAmount: getRevenue(resolvedJob),
                        salesAmount: getRevenue(resolvedJob),
                        sellingRate: getRevenue(resolvedJob),
                        totalAmount: getRevenue(resolvedJob),
                        actualRevenueTotal: getRevenue(resolvedJob),
                        expectedRevenueTotal: getRevenue(resolvedJob),
                        operationalCost: getOperationalCost(resolvedJob),
                        buyingRate: getBuyingRate(resolvedJob),
                        actualCostTotal: getCost(resolvedJob),
                        expectedCostTotal: getCost(resolvedJob),
                        totalCost: getCost(resolvedJob),
                        totalCostIncludesBuyingRate: true,
                        sourceType,
                        actualProfit: convertedProfit ?? 0,
                        expectedProfit: convertedProfit ?? 0,
                        currency: sellingCurrency,
                    },
                ],
            },
        });
    };

    const openLatestReturnInvoice = (job) => {
        const returnSummary = getReturnSummary(job);
        const returnInvoice = returnSummary.latestReturnInvoice;
        const returnInvoiceId = getEntityId(
            returnInvoice?._id || returnInvoice?.id,
        );
        const originalInvoiceId = getFinanceRecordId(job);

        if (!returnInvoiceId) {
            appAlert(financeText("No return invoice was found for this finance job."));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice,
                originalInvoice: job,
                originalInvoiceId,
            },
        });
    };

    const openPdf = async (job) => {
        // Open this synchronously from the click event so the browser does not
        // block it as an asynchronous popup while the PDF is being generated.
        const pdfWindow = window.open("", "_blank");

        if (pdfWindow) {
            pdfWindow.opener = null;
        }

        try {
            const resolvedJob = await resolveFinanceRecord(job);
            const doc = await createNetSellingRatePdf(resolvedJob);
            const invoiceNo = String(
                getPrimaryInvoice(resolvedJob)?.invoiceNo ||
                getPrimaryInvoice(resolvedJob)?.invoice_no ||
                resolvedJob?.jobNo ||
                "draft-invoice",
            ).replace(/[^a-zA-Z0-9_-]/g, "-");

            // Build a real application/pdf Blob explicitly. Some Chromium builds
            // open jsPDF's implicit Blob URL as an empty document.
            const pdfBuffer = doc.output("arraybuffer");
            if (!pdfBuffer?.byteLength) {
                throw new Error("Generated invoice PDF is empty.");
            }

            const pdfBlob = new Blob([pdfBuffer], {
                type: "application/pdf",
            });
            const pdfUrl = URL.createObjectURL(pdfBlob);

            if (pdfWindow) {
                // Do not navigate the popup directly to a Blob URL. Embed the
                // PDF in a normal document instead; this prevents the blank
                // `blob:` page that can occur in Edge/Chrome.
                const popupDocument = pdfWindow.document;
                popupDocument.open();
                popupDocument.write(
                    "<!doctype html><html><head><title>Net Selling Rate Invoice</title>" +
                    "<style>html,body{width:100%;height:100%;margin:0;background:#525659}" +
                    "iframe{display:block;width:100%;height:100%;border:0}</style>" +
                    "</head><body></body></html>",
                );
                popupDocument.close();

                const pdfFrame = popupDocument.createElement("iframe");
                pdfFrame.title = "Net Selling Rate Invoice PDF";
                pdfFrame.src = pdfUrl;
                popupDocument.body.appendChild(pdfFrame);
                window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
                return;
            }

            // Fallback for browsers that block popups despite a click action.
            const downloadLink = document.createElement("a");
            downloadLink.href = pdfUrl;
            downloadLink.download = `${invoiceNo}-net-selling-rate.pdf`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
        } catch (error) {
            if (pdfWindow && !pdfWindow.closed) {
                pdfWindow.close();
            }

            console.error("Net selling rate PDF error:", error);
            appAlert(error?.message || financeText("Invoice PDF could not be created."));
        }
    };

    const resetFilters = () => {
        setSearch("");
        setFinanceStatus("all");
        setCollectionStatus("all");
        setCreditRisk("all");
        setCustomerInvoiceStatus("all");
        setReturnStatus("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setInvoiceOrigin("all");
        setCurrencyFilter("all");
        setGeographicScope("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Date",
            "Customer",
            "Supplier",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Selling Currency",
            "Cost Currency",
            "Reporting Currency",
            "Revenue",
            "Cost",
            "Profit",
            "Margin",
            "Customer Invoice",
            "Return Status",
            "Return Invoice Count",
            "Returned Amount",
            "Reserved Return Amount",
            "Collection",
            "Credit Risk",
            "Risk Reason",
            "Provision Amount",
            "Risk Notes",
            "Finance Status",
        ];

        const rows = filteredJobs.map((job) => {
            const financials = getConvertedFinancials(job);
            const returnSummary = getReturnSummary(job);

            return [
                getShipmentNo(job),
                getDateValue(job) ? getDateValue(job).toLocaleDateString("en-US") : "",
                getCustomerName(job),
                getSupplierName(job),
                getGeographicLabel(job, partyCountryLookup),
                MODE_CONFIG[getTransportMode(job)]?.label || getTransportMode(job),
                getInvoiceOrigin(job) === "manual" ? "Manual" : "TMS",
                getCurrency(job),
                getCostCurrency(job),
                reportingCurrency,
                financials.revenue ?? financeText("Rate unavailable"),
                financials.cost ?? financeText("Rate unavailable"),
                financials.profit ?? financeText("Rate unavailable"),
                financials.margin === null
                    ? financeText("Rate unavailable")
                    : `${financials.margin.toFixed(2)}%`,
                getValue(
                    job,
                    "customerInvoiceStatus",
                    "customer_invoice_status",
                    "Issued",
                ),
                returnSummary.status,
                returnSummary.returnInvoices.length,
                returnSummary.returnedAmount,
                returnSummary.reservedReturnAmount,
                getValue(job, "collectionStatus", "collection_status", "Pending"),
                getRiskOption(job).label,
                getCollectionRisk(job).reason,
                getCollectionRisk(job).provisionAmount,
                getCollectionRisk(job).notes,
                getValue(job, "financeStatus", "finance_status", "Finance Open"),
            ];
        });

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        const modeName = transportMode === "all" ? "all-modes" : transportMode;
        const scopeName =
            geographicScope === "all" ? "all-regions" : geographicScope.toLowerCase();
        link.setAttribute(
            "download",
            `finance-jobs-${scopeName}-${modeName}-${todayValue}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("JOB / SHIPMENT"),
                    accessor: "jobNo",
                    width: "16%",
                    Cell: ({ row }) => {
                        const job = row.original;
                        const source =
                            getInvoiceOrigin(job) === "manual"
                                ? "Manual"
                                : getValue(job, "tmsSource", "tms_source", "TMS");

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.2} py={0.35}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",
                                        color: COLORS.blue,
                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <BusinessCenter sx={{ fontSize: "18px !important" }} />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 150,
                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openFinanceDetail(job)}
                                    >
                                        {getShipmentNo(job)}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        gap={0.55}
                                        mt={0.25}
                                    >
                                        <Chip
                                            size="small"
                                            label={MODE_CONFIG[getTransportMode(job)].label}
                                            sx={{
                                                height: 20,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                                color: MODE_CONFIG[getTransportMode(job)].color,
                                                background: MODE_CONFIG[getTransportMode(job)].light,
                                                border: `1px solid ${MODE_CONFIG[getTransportMode(job)].border}`,
                                                "& .MuiChip-label": { px: 0.8 },
                                            }}
                                        />
                                        <MDTypography
                                            variant="caption"
                                            noWrap
                                            sx={{ color: COLORS.subtext, maxWidth: 100 }}
                                        >
                                            {source} · {getInvoiceCount(job)} {financeText(getInvoiceCount(job) === 1 ? "customer invoice" : "customer invoices")}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CUSTOMER"),
                    accessor: "customerName",
                    width: "16%",
                    Cell: ({ row }) => {
                        const name = getCustomerName(row.original);
                        const sales = getValue(
                            row.original,
                            "salesPerson",
                            "sales_person",
                            "-",
                        );

                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.text,
                                            maxWidth: 145,
                                        }}
                                    >
                                        {name}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.subtext, maxWidth: 145 }}
                                    >
                                        {financeText(getGeographicLabel(row.original, partyCountryLookup))} · {financeText("Sales")}: {sales}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CUSTOMER INVOICES"),
                    accessor: "customerInvoices",
                    width: "13%",
                    Cell: ({ row }) => {
                        const job = row.original;
                        const invoices = getCustomerInvoices(job);
                        const count = getInvoiceCount(job);

                        return (
                            <MDBox>
                                <Chip
                                    size="small"
                                    label={`${count} ${financeText(count === 1 ? "invoice" : "invoices")}`}
                                    onClick={() => openFinanceDetail(job)}
                                    sx={{
                                        height: 25,
                                        color: COLORS.blue,
                                        background: COLORS.blueLight,
                                        border: "1px solid #B2DDFF",
                                        fontSize: "10px",
                                        fontWeight: 800,
                                        cursor: "pointer",
                                    }}
                                />

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.45}
                                    noWrap
                                    sx={{ color: COLORS.subtext, maxWidth: 145 }}
                                >
                                    {invoices
                                        .slice(0, 2)
                                        .map(
                                            (invoice) =>
                                                invoice?.invoiceNo ||
                                                invoice?.invoice_no ||
                                                invoice?.edm?.invoiceId ||
                                                (["issued", "approved", "received"].includes(
                                                    String(invoice?.invoiceStatus || "").toLowerCase(),
                                                )
                                                    ? financeText("No number yet")
                                                    : financeText("Draft")),
                                        )
                                        .join(", ")}
                                    {invoices.length > 2 ? ` +${invoices.length - 2}` : ""}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("REVENUE"),
                    accessor: "actualRevenueTotal",
                    align: "right",
                    Cell: ({ row }) => (
                        <MDBox textAlign="right">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.blue }}
                            >
                                {formatCompact(
                                    getBilledAmount(row.original, getRevenue(row.original)),
                                    getCurrency(row.original),
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {hasVatIncluded(row.original)
                                    ? `${financeText("Incl. VAT")} ${formatCompact(
                                        getVatAmount(row.original),
                                        getCurrency(row.original),
                                    )}`
                                    : financeText("Customer Billing")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("COST"),
                    accessor: "actualCostTotal",
                    align: "right",
                    Cell: ({ row }) => (
                        <MDBox textAlign="right">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.red }}
                            >
                                {formatCompact(
                                    getCost(row.original),
                                    getCostCurrency(row.original),
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Supplier Cost + Buying Rate")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("PROFITABILITY"),
                    accessor: "actualProfit",
                    align: "right",
                    Cell: ({ row }) => {
                        const financials = getConvertedFinancials(row.original);
                        const profit = financials.profit;
                        const margin = financials.margin;
                        const ratesAvailable = profit !== null;
                        const positive = profit >= 0;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: !ratesAvailable
                                            ? COLORS.amber
                                            : positive
                                                ? COLORS.green
                                                : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? formatCompact(profit, reportingCurrency)
                                        : financeText("Rate unavailable")}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    gap={0.4}
                                    mt={0.3}
                                >
                                    {positive ? (
                                        <ArrowUpward
                                            sx={{
                                                fontSize: "12px !important",
                                                color: COLORS.green,
                                            }}
                                        />
                                    ) : (
                                        <ArrowDownward
                                            sx={{
                                                fontSize: "12px !important",
                                                color: COLORS.red,
                                            }}
                                        />
                                    )}

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: positive ? COLORS.green : COLORS.red,
                                        }}
                                    >
                                        {ratesAvailable
                                            ? `${formatPercent(margin)} margin`
                                            : financeText("Missing exchange rate")}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CUSTOMER INVOICE"),
                    accessor: "customerInvoiceStatus",
                    width: "12%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={getValue(
                                row.original,
                                "customerInvoiceStatus",
                                "customer_invoice_status",
                                "Issued",
                            )}
                        />
                    ),
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "14%",
                    Cell: ({ row }) => {
                        const job = row.original;
                        const summary = getReturnSummary(job);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["Not Returned"];
                        const hasReturnInvoice = Boolean(
                            getEntityId(
                                summary.latestReturnInvoice?._id ||
                                summary.latestReturnInvoice?.id,
                            ),
                        );
                        const amount =
                            summary.returnedAmount > 0
                                ? summary.returnedAmount
                                : summary.reservedReturnAmount;
                        const amountLabel =
                            summary.returnedAmount > 0 ? financeText("returned") : financeText("reserved");

                        return (
                            <MDBox minWidth={132}>
                                <Tooltip
                                    title={
                                        hasReturnInvoice
                                            ? financeText("Open latest return invoice")
                                            : financeText("No return invoice")
                                    }
                                    arrow
                                >
                  <span>
                    <Chip
                        size="small"
                        label={financeText(summary.status)}
                        onClick={
                            hasReturnInvoice
                                ? () => openLatestReturnInvoice(job)
                                : undefined
                        }
                        sx={{
                            height: 24,
                            color: style.color,
                            background: style.background,
                            border: `1px solid ${style.border}`,
                            fontSize: "10px",
                            fontWeight: 800,
                            cursor: hasReturnInvoice ? "pointer" : "default",
                            "&:hover": {
                                background: style.background,
                                filter: hasReturnInvoice ? "brightness(.97)" : "none",
                            },
                        }}
                    />
                  </span>
                                </Tooltip>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.45}
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {summary.returnInvoices.length > 0
                                        ? `${summary.returnInvoices.length} document${
                                            summary.returnInvoices.length === 1 ? "" : "s"
                                        }${
                                            amount > 0
                                                ? ` · ${formatCompact(
                                                    amount,
                                                    getCurrency(job),
                                                )} ${amountLabel}`
                                                : ""
                                        }`
                                        : financeText("No return activity")}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COLLECTION"),
                    accessor: "collectionStatus",
                    width: "13%",
                    Cell: ({ row }) => {
                        const revenue = getBilledAmount(
                            row.original,
                            getRevenue(row.original),
                        );
                        const paid = getPaidAmount(row.original);
                        const rate = revenue ? Math.min((paid / revenue) * 100, 100) : 0;

                        const status = getValue(
                            row.original,
                            "collectionStatus",
                            "collection_status",
                            "Pending",
                        );

                        return (
                            <MDBox minWidth={112}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={0.7}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.text }}
                                    >
                                        {Math.round(rate)}%
                                    </MDTypography>

                                    <FinanceStatusChip status={status} />
                                </MDBox>

                                <LinearProgress
                                    variant="determinate"
                                    value={rate}
                                    sx={{
                                        height: 6,
                                        borderRadius: "999px",
                                        backgroundColor: "#EEF2F6",
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: "999px",
                                            background:
                                                rate >= 75
                                                    ? COLORS.green
                                                    : rate >= 35
                                                        ? COLORS.amber
                                                        : COLORS.red,
                                        },
                                    }}
                                />
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("FINANCE STATUS"),
                    accessor: "financeStatus",
                    width: "11%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={getValue(
                                row.original,
                                "financeStatus",
                                "finance_status",
                                "Finance Open",
                            )}
                        />
                    ),
                },
                {
                    Header: financeText("CREDIT RISK"),
                    accessor: "riskStatus",
                    width: "11%",
                    Cell: ({ row }) => {
                        const job = row.original;
                        const risk = getCollectionRisk(job);
                        const option = getRiskOption(job);

                        return (
                            <Tooltip
                                title={risk.reason || financeText("Open collection risk details")}
                                arrow
                            >
                                <Chip
                                    size="small"
                                    label={financeText(option.label)}
                                    onClick={() =>
                                        navigate("/invoice/finance/collections", {
                                            state: {
                                                invoiceId:
                                                    job?.invoiceId || job?.invoice?._id || job?._id,
                                                riskStatus: risk.status,
                                            },
                                        })
                                    }
                                    sx={{
                                        height: 25,
                                        color: option.color,
                                        background: option.background,
                                        border: `1px solid ${option.border}`,
                                        fontSize: "10px",
                                        fontWeight: 800,
                                        cursor: "pointer",
                                        "&:hover": {
                                            background: option.background,
                                            filter: "brightness(.97)",
                                        },
                                    }}
                                />
                            </Tooltip>
                        );
                    },
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "12%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const hasReturnInvoice = Boolean(
                            getEntityId(
                                summary.latestReturnInvoice?._id ||
                                summary.latestReturnInvoice?.id,
                            ),
                        );

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                                <Tooltip title={financeText("Open finance job detail")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openFinanceDetail(row.original)}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                            "&:hover": {
                                                background: "#D1E9FF",
                                            },
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title={
                                        getInvoiceCount(row.original) > 1
                                            ? financeText("Select an invoice from finance job detail")
                                            : financeText("Edit customer invoice")
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => openInvoiceEditor(row.original)}
                                        sx={{
                                            color: COLORS.amber,
                                            background: COLORS.amberLight,
                                            border: "1px solid #FEDF89",
                                            "&:hover": {
                                                background: "#FEF0C7",
                                            },
                                        }}
                                    >
                                        <Edit sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>

                                {hasReturnInvoice && (
                                    <Tooltip title={financeText("Open latest return invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openLatestReturnInvoice(row.original)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": {
                                                    background: "#EBE9FE",
                                                },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Tooltip title={financeText("View net selling rate PDF")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openPdf(row.original)}
                                        sx={{
                                            color: COLORS.red,
                                            background: COLORS.redLight,
                                            border: "1px solid #FECDCA",
                                            "&:hover": {
                                                background: "#FEE4E2",
                                            },
                                        }}
                                    >
                                        <PictureAsPdf sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>
                            </MDBox>
                        );
                    },
                },
            ],
            rows: filteredJobs,
        }),
        [
            filteredJobs,
            partyCountryLookup,
            getConvertedFinancials,
            reportingCurrency,
            financeLanguage,
        ],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{ background: "rgba(255,255,255,.12)" }}
                                    >
                                        <WorkspacePremium sx={{ color: "#FFFFFF", fontSize: 20 }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE FINANCE OPERATIONS")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Finance Jobs")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText("Manage shipment finance records, invoice lifecycle, profitability, collection status and operational exposure from one banking-grade workspace.")}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={<CheckCircle sx={{ color: "#75E0A7 !important" }} />}
                                        label={`${filteredJobs.length} ${financeText("finance records")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth sx={{ color: "#B2DDFF !important" }} />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "rgba(255,255,255,.72)" }}
                                        >
                                            {financeText("FILTERED PORTFOLIO PROFIT")}
                                        </MDTypography>

                                        <Paid sx={{ color: "#B2DDFF" }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{
                                            color: stats.profit >= 0 ? "#FFFFFF" : "#FECDCA",
                                        }}
                                    >
                                        {formatDisplayCompact(stats.profit, reportingCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: "rgba(255,255,255,.68)" }}
                                    >
                                        {financeText("Margin")} {formatPercent(stats.margin)} {financeText("across current filter result")}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "en" },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                    transition: "all .18s ease",
                                                    "&:hover": {
                                                        background:
                                                            financeLanguage === "en"
                                                                ? "#FFFFFF"
                                                                : "rgba(255,255,255,.12)",
                                                    },
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "tr" },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                    transition: "all .18s ease",
                                                    "&:hover": {
                                                        background:
                                                            financeLanguage === "tr"
                                                                ? "#FFFFFF"
                                                                : "rgba(255,255,255,.12)",
                                                    },
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                navigate("/invoice/finance/manual-invoices/new")
                                            }
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Add sx={{ mr: 0.5 }} />
                                            {financeText("Create Manual Invoice")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh finance jobs")}>
                                            <IconButton
                                                onClick={fetchFinanceJobs}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {financeText(option.label)}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            {MODE_CONFIG[mode]?.label || mode}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/*
                              * Separate from the "Currency" record filter
                              * directly above: this one does not narrow the
                              * result set, it only decides which currency the
                              * aggregated totals are rendered in.
                              */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                >
                                    {CURRENCY_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Return Status")}
                                    value={returnStatus}
                                    onChange={(event) => setReturnStatus(event.target.value)}
                                >
                                    {returnStatusOptions.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item === "all" ? financeText("All Return Statuses") : financeText(item)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit — one headline
                            figure instead of 7 equally-weighted cards. */}
                        <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Profit")}
                                    value={formatDisplayCompact(stats.profit, reportingCurrency)}
                                    tone={stats.profit >= 0 ? "green" : "red"}
                                    icon={<Paid />}
                                    meta={
                                        stats.ratesIncomplete
                                            ? financeText("Incomplete — exchange rate unavailable for some jobs")
                                            : `${financeText("Margin")} ${formatPercent(stats.margin)}`
                                    }
                                    context={[
                                        { label: financeText("Revenue"), value: formatDisplayCompact(stats.totalRevenue, reportingCurrency) },
                                        { label: financeText("Cost"), value: formatDisplayCompact(stats.totalCost, reportingCurrency) },
                                        { label: financeText("Collection"), value: formatPercent(stats.collectionRate) },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader title={financeText("Open Jobs")} />
                                        <MDTypography variant="h3" fontWeight="bold" sx={{ color: COLORS.text }}>
                                            {formatNumber(stats.openJobs)}
                                        </MDTypography>
                                        <MDTypography variant="caption" mb={2} sx={{ color: COLORS.subtext }}>
                                            {financeText("Finance job records")}
                                        </MDTypography>
                                        <MDBox display="flex" flexDirection="column" gap={1} mt="auto">
                                            {[
                                                { label: financeText("Supplier Waiting"), value: formatNumber(stats.waitingSupplierInvoice), tone: "amber" },
                                                { label: financeText("Customer Waiting"), value: formatNumber(stats.waitingCustomerInvoice), tone: "purple" },
                                                { label: financeText("High-Risk Exposure"), value: formatDisplayCompact(stats.highRiskExposure, reportingCurrency), tone: "red" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.1}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.25}>
                                                        <MDBox width={8} height={8} borderRadius="50%" sx={{ background: COLORS[row.tone] }} />
                                                        <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text }}>
                                                            {row.label}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone] }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>



                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <MDBox
                                                width={38}
                                                height={38}
                                                borderRadius="11px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <FilterAlt sx={{ fontSize: 19 }} />
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Finance Job Filters")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Search and segment finance operations by shipment, customer and status.")}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchFinanceJobs}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() => navigate("/invoice/finance")}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Dashboard")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6} lg={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, customer, email...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Finance Status")}
                                            value={financeStatus}
                                            onChange={(event) => setFinanceStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.finance.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Finance Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Collection Status")}
                                            value={collectionStatus}
                                            onChange={(event) =>
                                                setCollectionStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.collection.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Collection Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Customer Invoice")}
                                            value={customerInvoiceStatus}
                                            onChange={(event) =>
                                                setCustomerInvoiceStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.customerInvoice.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Invoice Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={returnStatus}
                                            onChange={(event) => setReturnStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {returnStatusOptions.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Return Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Credit Risk")}
                                            value={creditRisk}
                                            onChange={(event) => setCreditRisk(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Credit Risks")}</MenuItem>
                                            {RISK_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {financeText(option.label)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        Showing{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredJobs.length}
                                        </strong>{" "}
                                        of{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {jobs.length}
                                        </strong>{" "}
                                        finance records
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            size="small"
                                            label={`Revenue ${formatCompact(stats.totalRevenue)}`}
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`Profit ${formatCompact(stats.profit, reportingCurrency)}`}
                                            sx={{
                                                color: stats.profit >= 0 ? COLORS.green : COLORS.red,
                                                background:
                                                    stats.profit >= 0
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1500,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="jobs"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={2.5} mt={0.1}>
                            {Object.entries(transportSummary).map(([mode, data]) => (
                                <Grid item xs={12} md={4} key={mode}>
                                    <TransportModeCard
                                        mode={mode}
                                        data={data}
                                        totalJobs={filteredJobs.length}
                                        currency={reportingCurrency}
                                        formatAmount={formatDisplayCompact}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.5}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Revenue by Transport Mode")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Revenue contribution of Ocean, Road and Air operations")}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <TrendingUp />
                                            </MDBox>
                                        </MDBox>
                                        <ModeComparisonChart
                                            summary={transportSummary}
                                            currency={reportingCurrency}
                                            formatAmount={formatDisplayCompact}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.2}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment Type Mix")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Most frequently used shipment services")}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                }}
                                            >
                                                <Inventory2 />
                                            </MDBox>
                                        </MDBox>

                                        {shipmentTypeSummary.length ? (
                                            shipmentTypeSummary.map(([type, count], index) => {
                                                const max = shipmentTypeSummary[0]?.[1] || 1;
                                                return (
                                                    <MDBox key={type} mb={1.55}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            mb={0.55}
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {index + 1}. {type}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.purple }}
                                                            >
                                                                {count} jobs
                                                            </MDTypography>
                                                        </MDBox>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(count / max) * 100}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#EEF2F6",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    backgroundColor: COLORS.purple,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })
                                        ) : (
                                            <MDBox py={4} textAlign="center">
                                                <MDTypography
                                                    variant="body2"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type data available.")}
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
};

export default FinanceJobs;